import React from 'react';
import { ConfirmationModal } from './ConfirmationModal';
import { threadListCopy } from './threadListCopy';
import { categoryLabel, type ThreadCategory, type ThreadListLang } from '../../types/threadCategories';

/**
 * SectionMergeConfirm — "«Clients» already has that name. move everything
 * there?" (2026-09-16, Miles: "I tried to change a category name and it didn't
 * allow me to press enter because a category already had that name. it should
 * allow me to and confirm with the user really quick (same style as the rest of
 * the platform) and then when user confirms real quick moves all threads in
 * that category under the category that already exists").
 *
 * The platform's own `ConfirmationModal`, not a bespoke sheet — same style, same
 * Escape/Enter behaviour, same body portal at the app overlay rung. Shared by the
 * rail's section header AND the sections manager so the two cannot answer the
 * same question differently.
 *
 * It is `isInfo`, never `isDangerous`: nothing is destroyed. Every thread keeps
 * existing, keeps its own name, and is one drag from moving back — and this is
 * the viewer's own private arrangement, so nobody else's rail moves at all.
 */
export interface SectionMergeConfirmProps {
  /** The section being renamed — null when nothing is being asked. */
  from: ThreadCategory | null;
  /** The section that already wears the name. */
  into: ThreadCategory | null;
  lang: ThreadListLang;
  onConfirm: () => void;
  onCancel: () => void;
}

export const SectionMergeConfirm: React.FC<SectionMergeConfirmProps> = ({ from, into, lang, onConfirm, onCancel }) => {
  const copy = threadListCopy(lang);
  return (
    <ConfirmationModal
      isOpen={!!from && !!into}
      title={copy.mergeSectionsTitle}
      message={copy.mergeSectionsMessage(
        from ? categoryLabel(from, lang) : '',
        into ? categoryLabel(into, lang) : '',
      )}
      confirmText={copy.mergeSectionsConfirm}
      cancelText={copy.mergeSectionsCancel}
      isInfo
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
};

export default SectionMergeConfirm;
